import { useState, useEffect, useRef } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Download, Upload, FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const DataImportExportSection = () => {
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(''); // `${key}:${action}` while a request is in flight
  const [importResult, setImportResult] = useState(null);
  const fileInputRefs = useRef({});

  useEffect(() => {
    API.get('/data/categories').then(res => setCategories(res.data)).catch(() => {});
  }, []);

  const downloadFile = async (url, filename) => {
    const res = await API.get(url, { responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleTemplate = async (key) => {
    setBusy(`${key}:template`);
    try {
      await downloadFile(`/data/template/${key}`, `${key}_template.csv`);
    } catch {
      toast.error('Could not download template');
    } finally {
      setBusy('');
    }
  };

  const handleExport = async (key) => {
    setBusy(`${key}:export`);
    try {
      await downloadFile(`/data/export/${key}`, `${key}_export.csv`);
      toast.success('Export downloaded');
    } catch {
      toast.error('Export failed');
    } finally {
      setBusy('');
    }
  };

  const handleImport = async (key, file) => {
    if (!file) return;
    setBusy(`${key}:import`);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await API.post(`/data/import/${key}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult({ key, ...res.data });
      toast.success(`Imported ${res.data.created} record(s)${res.data.skipped ? `, skipped ${res.data.skipped} duplicate(s)` : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setBusy('');
    }
  };

  return (
    <Card className="border-slate-200 dark:border-slate-800" data-testid="data-import-export-section">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <FileSpreadsheet className="w-5 h-5 text-teal-600" /> Data Import & Export
        </CardTitle>
        <CardDescription>Download a CSV template, export your existing data, or bulk-import historical records</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {categories.map((cat) => (
          <div key={cat.key} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg border border-slate-200 dark:border-slate-800" data-testid={`data-io-row-${cat.key}`}>
            <span className="text-sm font-medium text-slate-900 dark:text-white">{cat.label}</span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleTemplate(cat.key)}
                disabled={busy === `${cat.key}:template`}
                data-testid={`data-io-template-${cat.key}`}
              >
                {busy === `${cat.key}:template` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Template'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleExport(cat.key)}
                disabled={busy === `${cat.key}:export`}
                data-testid={`data-io-export-${cat.key}`}
              >
                {busy === `${cat.key}:export` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Download className="w-3.5 h-3.5 mr-1" /> Export</>}
              </Button>
              {!cat.export_only && (
                <>
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    ref={(el) => { fileInputRefs.current[cat.key] = el; }}
                    onChange={(e) => { handleImport(cat.key, e.target.files?.[0]); e.target.value = ''; }}
                    data-testid={`data-io-import-input-${cat.key}`}
                  />
                  <Button
                    size="sm"
                    disabled={busy === `${cat.key}:import`}
                    onClick={() => fileInputRefs.current[cat.key]?.click()}
                    data-testid={`data-io-import-${cat.key}`}
                  >
                    {busy === `${cat.key}:import` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5 mr-1" /> Import</>}
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
        {importResult && (
          <div className="mt-2 p-3 rounded-lg bg-teal-50 dark:bg-teal-900/20 text-sm" data-testid="data-io-import-result">
            <p className="font-medium text-teal-700 dark:text-teal-300">
              {categories.find(c => c.key === importResult.key)?.label}: {importResult.created} imported
              {importResult.skipped ? `, ${importResult.skipped} skipped (duplicates)` : ''}
            </p>
            {importResult.errors?.length > 0 && (
              <ul className="mt-1 text-xs text-amber-600 dark:text-amber-400 list-disc pl-4 max-h-24 overflow-y-auto">
                {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
